import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export const allocationProposalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people/allocation-proposal',
  component: AllocationProposalPage,
});

function currentMonth(): string {
  const now = new Date();
  const ist = new Date(now.getTime() + (5 * 60 + 30) * 60 * 1000);
  return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, '0')}`;
}

function AllocationProposalPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [month, setMonth] = React.useState(currentMonth);

  const proposalQuery = useQuery({
    queryKey: ['work-log', 'proposal', month],
    queryFn: () => api.getAllocationProposal(month),
  });

  const confirmMutation = useMutation({
    mutationFn: () => api.confirmAllocationProposal(month),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['work-log', 'proposal'] });
    },
  });

  const proposal = proposalQuery.data;
  type ProposalRow = NonNullable<typeof proposal>['rows'][number];
  const byPerson = React.useMemo(() => {
    const map = new Map<string, { name: string; rows: ProposalRow[] }>();
    for (const row of proposal?.rows ?? []) {
      const entry = map.get(row.personId) ?? { name: row.personName ?? row.personId, rows: [] };
      entry.rows.push(row);
      map.set(row.personId, entry);
    }
    return [...map.values()];
  }, [proposal]);

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-4xl space-y-6 p-6">
        <div>
          <h1 className="font-display text-4xl font-semibold md:text-5xl">
            Allocation proposal
          </h1>
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-[hsl(var(--muted-foreground))]">
            Derived from declared work-log minutes. Hours never post money on their own — confirming
            writes the matrix that payroll reads.
          </p>
        </div>

        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <label htmlFor="month" className="text-xs uppercase tracking-wider">
              Month
            </label>
            <Input
              id="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              placeholder="YYYY-MM"
              className="w-40"
            />
          </div>
        </div>

        {confirmMutation.error ? (
          <div className="rounded-lg border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-4 py-3 text-sm">
            {toMessage(confirmMutation.error)}
          </div>
        ) : null}

        {confirmMutation.data ? (
          <div className="rounded-lg border border-[hsl(var(--border))] px-4 py-3 text-sm">
            Wrote {confirmMutation.data.written} allocation row(s) for {confirmMutation.data.month}.
          </div>
        ) : null}

        {proposal === undefined ? null : proposal.rows.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Nothing to propose</CardTitle>
              <CardDescription>
                No project time was declared in {proposal.month}.
                {proposal.unmappedMinutes > 0
                  ? ` ${proposal.unmappedMinutes} minute(s) are still unmapped.`
                  : ''}
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <>
            {byPerson.map((person) => (
              <Card key={person.name}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{person.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 pt-0 text-sm">
                  {person.rows.map((row) => (
                    <div key={row.projectId} className="flex items-center gap-3">
                      <span className="flex-1">{row.projectName ?? row.projectId}</span>
                      <span className="text-[hsl(var(--muted-foreground))] tabular-nums">
                        {row.declaredMinutes} min
                      </span>
                      <span className="w-14 text-right font-medium tabular-nums">
                        {row.percent}%
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}

            {proposal.unmappedMinutes > 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {proposal.unmappedMinutes} unmapped minute(s) are excluded — Overhead is a cost
                centre, not a project.
              </p>
            ) : null}

            {proposal.blockedReason ? (
              <div className="rounded-lg border border-[hsl(var(--border))] px-4 py-3 text-sm">
                {proposal.blockedReason}
              </div>
            ) : null}

            <Button
              disabled={!proposal.confirmable || confirmMutation.isPending}
              onClick={() => confirmMutation.mutate()}
            >
              {confirmMutation.isPending ? 'Confirming…' : 'Confirm allocation matrix'}
            </Button>
          </>
        )}
      </div>
    </AppShell>
  );
}
